import React, { useState, useEffect } from 'react';
import { NavLink, useLocation } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { motion } from 'framer-motion';
import { PulsingLoader } from '@/components/ui/pulsing-loader';
import {
  Users,
  CreditCard,
  MapPin,
  ChevronRight,
  ChevronDown,
  Home,
  Church,
  BarChart3,
  Building2,
  Calendar,
  Coins,
  Shield,
  Settings,
  Receipt,
  Vote,
  ShieldCheck,
  UserCheck,
} from 'lucide-react';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/authContext';
import { usePermissions } from '@/hooks/usePermissions';
import { getDistrictsWithRegionsTree } from '@/data/territoryRepository';
import { getInstitutions } from '@/Services/institutions.service';
import { getInstitutionConfig } from '@/features/institutions/config/institutions';
import type { District } from '@/models/district';
import type { Institution } from '@/features/institutions/types';

interface SidebarItemProps {
  to: string;
  icon: React.ElementType;
  children: React.ReactNode;
  level?: number;
  color?: 'primary' | 'accent' | 'secondary' | 'success' | 'info' | 'warning' | 'rose' | 'teal';
  disabled?: boolean;
}

function SidebarItem({
  to,
  icon: Icon,
  children,
  level = 0,
  color = 'primary',
  disabled = false,
}: SidebarItemProps) {
  const paddingLeft = `${level * 1}rem`;

  const colorClasses = {
    primary: {
      hover: 'hover:from-primary/15 hover:to-primary/5 hover:text-primary',
      active: 'from-primary/15 to-primary/5 text-primary border-primary',
    },
    accent: {
      hover: 'hover:from-accent/15 hover:to-accent/5 hover:text-accent',
      active: 'from-accent/15 to-accent/5 text-accent border-accent',
    },
    secondary: {
      hover:
        'hover:from-secondary/15 hover:to-secondary/5 hover:text-secondary',
      active:
        'from-secondary/15 to-secondary/5 text-secondary border-secondary',
    },
    success: {
      hover: 'hover:from-success/15 hover:to-success/5 hover:text-success',
      active: 'from-success/15 to-success/5 text-success border-success',
    },
    info: {
      hover: 'hover:from-info/15 hover:to-info/5 hover:text-info',
      active: 'from-info/15 to-info/5 text-info border-info',
    },
    warning: {
      hover: 'hover:from-warning/15 hover:to-warning/5 hover:text-warning',
      active: 'from-warning/15 to-warning/5 text-warning border-warning',
    },
    rose: {
      hover: 'hover:from-rose/15 hover:to-rose/5 hover:text-rose',
      active: 'from-rose/15 to-rose/5 text-rose border-rose',
    },
    teal: {
      hover: 'hover:from-teal/15 hover:to-teal/5 hover:text-teal',
      active: 'from-teal/15 to-teal/5 text-teal border-teal',
    },
  };

  if (disabled) {
    return (
      <div
        className={cn(
          'mx-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold',
          'cursor-not-allowed text-muted-foreground opacity-50',
        )}
        style={{ paddingLeft: level > 0 ? paddingLeft : undefined }}
      >
        <Icon className="size-4 shrink-0" />
        <span className="flex-1 truncate">{children}</span>
        <span className="rounded bg-muted px-1.5 py-0.5 text-[10px] font-medium text-muted-foreground">
          Próximamente
        </span>
      </div>
    );
  }

  return (
    <NavLink
      to={to}
      end // Don't match child routes
      className={({ isActive }) =>
        cn(
          'mx-1 flex items-center gap-3 rounded-xl px-3 py-2.5 text-sm font-semibold transition-all duration-300',
          'hover:scale-[1.02] hover:bg-gradient-to-r hover:shadow-md',
          colorClasses[color].hover,
          isActive
            ? `bg-gradient-to-r ${colorClasses[color].active} border-l-2 shadow-md`
            : 'text-muted-foreground',
        )
      }
      style={{ paddingLeft: level > 0 ? paddingLeft : undefined }}
    >
      <Icon className="size-4 shrink-0" />
      <span className="truncate">{children}</span>
    </NavLink>
  );
}

interface CollapsibleSectionProps {
  title: string;
  icon: React.ElementType;
  defaultExpanded?: boolean;
  children: React.ReactNode;
  color?: 'primary' | 'accent' | 'secondary' | 'success' | 'info' | 'warning' | 'rose' | 'teal';
}

function CollapsibleSection({
  title,
  icon: Icon,
  defaultExpanded = false,
  children,
  color = 'primary',
}: CollapsibleSectionProps) {
  const [isExpanded, setIsExpanded] = useState(defaultExpanded);

  const colorClasses = {
    primary: 'hover:from-primary/20 hover:to-primary/5 hover:text-primary',
    accent: 'hover:from-accent/20 hover:to-accent/5 hover:text-accent',
    secondary:
      'hover:from-secondary/20 hover:to-secondary/5 hover:text-secondary',
    success: 'hover:from-success/20 hover:to-success/5 hover:text-success',
    info: 'hover:from-info/20 hover:to-info/5 hover:text-info',
    warning: 'hover:from-warning/20 hover:to-warning/5 hover:text-warning',
    rose: 'hover:from-rose/20 hover:to-rose/5 hover:text-rose',
    teal: 'hover:from-teal/20 hover:to-teal/5 hover:text-teal',
  };

  return (
    <div>
      <motion.button
        onClick={() => setIsExpanded(!isExpanded)}
        className={cn(
          'mx-1 flex w-full items-center gap-2 rounded-lg px-3 py-2.5 text-sm font-medium transition-all duration-300',
          'text-muted-foreground hover:bg-gradient-to-r',
          'hover:scale-[1.01]',
          colorClasses[color],
        )}
        whileHover={{ x: 2 }}
        whileTap={{ scale: 0.98 }}
      >
        <Icon className="size-4 shrink-0" />
        <span className="flex-1 truncate text-left">{title}</span>
        <motion.div
          animate={{ rotate: isExpanded ? 0 : -90 }}
          transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        >
          <ChevronDown className="size-4 shrink-0" />
        </motion.div>
      </motion.button>
      <motion.div
        initial={false}
        animate={{
          height: isExpanded ? 'auto' : 0,
          opacity: isExpanded ? 1 : 0,
        }}
        transition={{ duration: 0.3, ease: [0.25, 0.1, 0.25, 1] }}
        style={{ overflow: 'hidden' }}
      >
        <div className="mt-1 space-y-1 pl-2">{children}</div>
      </motion.div>
    </div>
  );
}

interface DistrictItemProps {
  district: District;
  level?: number;
}

function DistrictItem({ district, level = 1 }: DistrictItemProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const hasRegions = district.regions && district.regions.length > 0;
  const location = useLocation();

  // Extract current tab from URL (information, ministers, churches, credentials)
  const getCurrentTab = () => {
    const pathParts = location.pathname.split('/');
    const lastPart = pathParts[pathParts.length - 1];
    return ['ministers', 'churches', 'credentials'].includes(lastPart)
      ? lastPart
      : 'information';
  };

  const currentTab = getCurrentTab();

  return (
    <div>
      <div className="flex items-center gap-1">
        {hasRegions && (
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="rounded p-1 hover:bg-primary/10 hover:text-primary"
          >
            {isExpanded ? (
              <ChevronDown className="size-3" />
            ) : (
              <ChevronRight className="size-3" />
            )}
          </button>
        )}
        <NavLink
          to={`/territories/districts/${district.id}/${currentTab}`}
          className={({ isActive }) =>
            cn(
              'flex flex-1 items-center gap-2 rounded-lg px-3 py-2 text-sm transition-colors',
              'hover:bg-primary/10 hover:text-primary',
              isActive
                ? 'bg-primary/15 font-medium text-primary'
                : 'text-muted-foreground',
              !hasRegions && 'ml-5',
            )
          }
          style={{ paddingLeft: `${level * 0.5}rem` }}
        >
          <MapPin className="size-3 shrink-0" />
          <span className="truncate">{district.name}</span>
        </NavLink>
      </div>
      {isExpanded && hasRegions && (
        <div className="ml-4 mt-1 space-y-1 border-l border-border pl-2">
          {district.regions!.map((region) => (
            <NavLink
              key={region.id}
              to={`/territories/regions/${region.id}/${currentTab}`}
              className={({ isActive }) =>
                cn(
                  'flex items-center gap-2 rounded-lg px-3 py-1.5 text-xs transition-colors',
                  'hover:bg-primary/10 hover:text-primary',
                  isActive
                    ? 'bg-primary/15 font-medium text-primary'
                    : 'text-muted-foreground',
                )
              }
            >
              <div className="size-1.5 shrink-0 rounded-full bg-current" />
              <span className="truncate">{region.name}</span>
            </NavLink>
          ))}
        </div>
      )}
    </div>
  );
}

export function AppSidebar() {
  const { user: authUser } = useAuth();
  const [districts, setDistricts] = useState<District[]>([]);
  const [institutions, setInstitutions] = useState<Institution[]>([]);
  const [loading, setLoading] = useState(true);

  const { can } = usePermissions();
  const canSeeMinisters = can('minister.view');
  const canSeeCredentials = can('credential_request.view');
  const canSeeReports = can('report.view');
  const canSeeEvents = can('event.view');
  // tithe.view_own: authorities see only their own tithes
  const canSeeTithes = can('tithe.view') || can('tithe.view_own');
  const canSeeReceipts = can('receipt.view');
  const canSeeSettings = can('bank_account.view');
  const canSeeFinance = canSeeTithes || canSeeReceipts || canSeeSettings;
  const canSeeChurches = can('church.view');
  const canSeeVoting = can('election.view');
  const canCheckinVoters = can('election.checkin');
  const canManageRoles = can('role.manage');
  const canManageUsers = can('user.manage');

  useEffect(() => {
    const fetchData = async () => {
      if (!authUser) return;

      try {
        const [districtsData, institutionsData] = await Promise.all([
          getDistrictsWithRegionsTree(),
          getInstitutions(),
        ]);
        setDistricts(districtsData);
        setInstitutions(institutionsData);
      } catch (error) {
        console.error('Error fetching sidebar data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authUser]);

  if (loading) {
    return (
      <aside className="flex h-full w-72 flex-col items-center justify-center border-r border-border bg-gradient-to-br from-card via-card/95 to-primary/5 lg:w-80">
        <PulsingLoader size="md" label="Cargando..." />
      </aside>
    );
  }

  return (
    <aside className="relative flex h-full w-72 flex-col border-r border-border bg-gradient-to-br from-card via-card/95 to-primary/5 shadow-lg backdrop-blur-sm lg:w-80">
      {/* Animated gradient overlay */}
      <div className="pointer-events-none absolute inset-0 bg-gradient-to-br from-primary/0 via-transparent to-accent/5 opacity-50" />

      {/* Navigation */}
      <ScrollArea className="relative flex-1 px-3 py-4">
        <motion.nav
          className="space-y-4"
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.4, staggerChildren: 0.1 }}
        >
          {/* Dashboard/Home */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
          >
            <div className="space-y-1">
              <SidebarItem to="/" icon={Home}>
                Dashboard
              </SidebarItem>
            </div>
          </motion.div>

          <Separator />

          {/* Territory Management Section */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
          >
            <h3 className="mb-2 rounded bg-gradient-to-r from-primary/40 via-primary/20 to-transparent px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-primary/90">
              Organización
            </h3>
            <div className="space-y-1">
              <SidebarItem to="/directorio-nacional" icon={Shield} color="primary">
                Directorio Nacional
              </SidebarItem>
              <CollapsibleSection
                title="Instituciones y Departamentos"
                icon={Building2}
                defaultExpanded={false}
                color="primary"
              >
                <div className="space-y-1">
                  {institutions.filter((i) => i.slug !== "directorio-nacional").map((institution) => {
                    const config = getInstitutionConfig(institution.slug);
                    return (
                      <SidebarItem
                        key={institution.id}
                        to={`/institutions/${institution.slug}`}
                        icon={config?.icon ?? Building2}
                        color="primary"
                      >
                        {institution.name}
                      </SidebarItem>
                    );
                  })}
                </div>
              </CollapsibleSection>
              <CollapsibleSection
                title="Organización Territorial"
                icon={MapPin}
                defaultExpanded={true}
                color="primary"
              >
                <div className="space-y-1">
                  {districts.map((district, index) => (
                    <motion.div
                      key={district.id}
                      initial={{ opacity: 0, x: -10 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: 0.3 + index * 0.05 }}
                    >
                      <DistrictItem district={district} />
                    </motion.div>
                  ))}
                </div>
              </CollapsibleSection>
            </div>
          </motion.div>

          <Separator />

          {/* Ministers Section */}
          {canSeeMinisters && (
            <>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 }}
              >
                <h3 className="mb-2 rounded bg-gradient-to-r from-accent/40 via-accent/20 to-transparent px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-accent/90">
                  Ministros
                </h3>
                <div className="space-y-1">
                  <SidebarItem to="/ministers" icon={Users} color="accent">
                    Todos los Ministros
                  </SidebarItem>
                </div>
              </motion.div>

              <Separator />
            </>
          )}

          {/* Credentials Section */}
          {canSeeCredentials && (
            <>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 }}
              >
                <h3 className="mb-2 rounded bg-gradient-to-r from-success/40 via-success/20 to-transparent px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-success/90">
                  Credenciales
                </h3>
                <div className="space-y-1">
                  <SidebarItem
                    to="/credential-requests"
                    icon={CreditCard}
                    color="success"
                  >
                    Credenciales Ministeriales
                  </SidebarItem>
                  <SidebarItem
                    to="/credential-requests/function"
                    icon={CreditCard}
                    color="success"
                  >
                    Credenciales de Función
                  </SidebarItem>
                </div>
              </motion.div>

              <Separator />
            </>
          )}

          {/* Reports Section */}
          {canSeeReports && (
            <>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 }}
              >
                <h3 className="mb-2 rounded bg-gradient-to-r from-secondary/40 via-secondary/20 to-transparent px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-secondary/90">
                  Reportes
                </h3>
                <div className="space-y-1">
                  <SidebarItem to="/reports" icon={BarChart3} color="secondary">
                    Reportes
                  </SidebarItem>
                </div>
              </motion.div>

              <Separator />
            </>
          )}

          {/* Events and Finance sections, each by its own permissions */}
          {(canSeeEvents || canSeeFinance) && (
            <>
              {canSeeEvents && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.7 }}
              >
                <h3 className="mb-2 rounded bg-gradient-to-r from-warning/40 via-warning/20 to-transparent px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-warning/90">
                  Eventos
                </h3>
                <div className="space-y-1">
                  <SidebarItem to="/events" icon={Calendar} color="warning">
                    Todos los Eventos
                  </SidebarItem>
                </div>
              </motion.div>
              )}

              {canSeeFinance && (
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.75 }}
              >
                <h3 className="mb-2 rounded bg-gradient-to-r from-rose/40 via-rose/20 to-transparent px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-rose/90">
                  Finanzas
                </h3>
                <div className="space-y-1">
                  {canSeeTithes && (
                    <SidebarItem to="/tithes" icon={Coins} color="rose">
                      Diezmos
                    </SidebarItem>
                  )}
                  {canSeeReceipts && (
                    <SidebarItem to="/receipts" icon={Receipt} color="rose">
                      Recibos
                    </SidebarItem>
                  )}
                  {canSeeSettings && (
                    <SidebarItem to="/settings" icon={Settings} color="rose">
                      Configuración
                    </SidebarItem>
                  )}
                </div>
              </motion.div>
              )}

              <Separator />
            </>
          )}

          {/* Voting - follows the election.view permission (admins; later kiosk volunteers) */}
          {(canSeeVoting || canCheckinVoters) && (
            <>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.8 }}
              >
                <h3 className="mb-2 rounded bg-gradient-to-r from-teal/40 via-teal/20 to-transparent px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-teal/90">
                  Votaciones
                </h3>
                <div className="space-y-1">
                  {canSeeVoting && (
                    <SidebarItem to="/voting" icon={Vote} color="teal">
                      Elecciones
                    </SidebarItem>
                  )}
                  {canCheckinVoters && (
                    <SidebarItem to="/voting/checkin" icon={UserCheck} color="teal">
                      Habilitar votantes
                    </SidebarItem>
                  )}
                </div>
              </motion.div>

              <Separator />
            </>
          )}

          {/* Administration: users (user.manage) and roles (role.manage) */}
          {(canManageRoles || canManageUsers) && (
            <>
              <motion.div
                initial={{ opacity: 0, y: -10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.85 }}
              >
                <h3 className="mb-2 rounded bg-gradient-to-r from-secondary/40 via-secondary/20 to-transparent px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-secondary/90">
                  Administración
                </h3>
                <div className="space-y-1">
                  {canManageUsers && (
                    <SidebarItem to="/users" icon={Users} color="secondary">
                      Usuarios
                    </SidebarItem>
                  )}
                  {canManageRoles && (
                    <SidebarItem to="/roles" icon={ShieldCheck} color="secondary">
                      Roles y permisos
                    </SidebarItem>
                  )}
                </div>
              </motion.div>

              <Separator />
            </>
          )}

          {/* Churches Section - Coming Soon */}
          {canSeeChurches && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.8 }}
            >
              <h3 className="mb-2 rounded bg-gradient-to-r from-info/40 via-info/20 to-transparent px-4 py-1.5 text-xs font-semibold uppercase tracking-wider text-info/90">
                Iglesias
              </h3>
              <div className="space-y-1">
                <SidebarItem to="#" icon={Church} color="info" disabled>
                  Todas las Iglesias
                </SidebarItem>
              </div>
            </motion.div>
          )}
        </motion.nav>
      </ScrollArea>
    </aside>
  );
}
